import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Phone, MessageSquare } from "lucide-react";
import { BRAND } from "@/data/content";
import SEO from "@/components/SEO";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch {
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-ng-bg hex-bg flex items-center justify-center px-6 py-16">
      <SEO title="Payment Confirmation" robots="noindex, nofollow" />
      <div
        data-testid="success-page"
        className="w-full max-w-xl border border-ng-line bg-ng-surface p-10 md:p-12 text-center shadow-[0_0_80px_-20px_rgba(30,144,255,0.5)]"
      >
        {state === "polling" && (
          <>
            <Loader2 size={42} className="mx-auto text-ng-blue animate-spin mb-6" />
            <h1 className="font-display font-bold uppercase text-3xl text-white">
              Confirming payment&hellip;
            </h1>
            <p className="text-white/60 mt-3 font-light text-sm">
              Hang tight. Verifying with Stripe.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-ng-blue mb-6" data-testid="success-icon" />
            <div className="label-eyebrow mb-2">Payment Confirmed</div>
            <h1 className="font-display font-bold uppercase text-3xl md:text-4xl text-white tracking-tight">
              Welcome to <span className="text-ng-blue">NEXTGEN</span>.
            </h1>
            <p className="text-white/70 mt-4 font-light leading-relaxed">
              Your Foundation Package is locked in. Coach J will personally reach out within{" "}
              <span className="text-ng-blue font-semibold">24 hours</span> to onboard you and kick off your custom program.
            </p>

            {info && (
              <div className="mt-8 border border-ng-line bg-ng-bg p-5 inline-block text-left">
                <div className="label-eyebrow">Receipt</div>
                <div className="mt-2 text-white font-display font-bold text-2xl">
                  ${(info.amount_total / 100).toFixed(2)} {info.currency?.toUpperCase()}
                </div>
                <div className="text-white/50 text-xs mt-1">
                  Foundation Package &middot; First Month
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`tel:${BRAND.phone_link}`}
                className="inline-flex items-center justify-center gap-2 bg-ng-blue text-white px-5 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors"
              >
                <Phone size={14} /> Call Coach J
              </a>
              <a
                href={`sms:${BRAND.phone_link}`}
                className="inline-flex items-center justify-center gap-2 border border-ng-line text-white px-5 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display hover:border-ng-blue hover:text-ng-blue transition-colors"
              >
                <MessageSquare size={14} /> Text Coach J
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 text-white/60 hover:text-ng-blue px-5 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display transition-colors"
              >
                <ArrowLeft size={14} /> Back to site
              </Link>
            </div>
          </>
        )}

        {state === "expired" && (
          <>
            <XCircle size={48} className="mx-auto text-yellow-400 mb-6" />
            <h1 className="font-display font-bold uppercase text-3xl text-white">
              Session expired
            </h1>
            <p className="text-white/60 mt-3 font-light text-sm">
              The checkout session expired without payment. Start again any time.
            </p>
            <Link
              to="/#programs"
              className="mt-8 inline-flex items-center gap-2 bg-ng-blue text-white px-6 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display"
            >
              Back to programs
            </Link>
          </>
        )}

        {state === "timeout" && (
          <>
            <Loader2 size={42} className="mx-auto text-yellow-400 mb-6" />
            <h1 className="font-display font-bold uppercase text-2xl text-white">
              Still processing
            </h1>
            <p className="text-white/60 mt-3 font-light text-sm">
              Stripe is taking a moment. You&rsquo;ll receive a confirmation email shortly.
              If anything seems off, text Coach J at {BRAND.phone}.
            </p>
            <Link
              to="/"
              className="mt-6 inline-block text-ng-blue text-xs uppercase tracking-widest font-display border-b border-ng-blue/40 pb-1"
            >
              Back to site
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-red-400 mb-6" />
            <h1 className="font-display font-bold uppercase text-2xl text-white">
              Something went wrong
            </h1>
            <p className="text-white/60 mt-3 font-light text-sm">
              We couldn&rsquo;t confirm your payment status. Text Coach J at {BRAND.phone} and we&rsquo;ll sort it.
            </p>
            <Link
              to="/"
              className="mt-6 inline-block text-ng-blue text-xs uppercase tracking-widest font-display border-b border-ng-blue/40 pb-1"
            >
              Back to site
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
